import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

function VendorLogo({ vendorName, logoPath }) {
  const [hasError, setHasError] = useState(false);

  if (hasError || !logoPath) {
    return (
      <div className="h-12 w-16 flex items-center justify-center text-gray-400 text-xs font-medium text-center leading-tight">
        {vendorName?.length > 30
          ? vendorName?.slice(0, 30) + "..."
          : vendorName}
      </div>
    );
  }

  return (
    <img
      src={logoPath}
      alt={vendorName}
      className="h-12 w-16 object-contain"
      onError={() => setHasError(true)}
    />
  );
}

export const TASummary = ({
  vendorSummaryData,
  availableTherapeuticAreas,
  summaryFilters,
  onChangeSummaryFilters,
}) => {
  const navigate = useNavigate();
  const [selectedTA, setSelectedTA] = useState(
    summaryFilters.selectedTA || "All"
  );
  const [taSelectorOpen, setTaSelectorOpen] = useState(false);
  const [selectedKeyMetric, setSelectedKeyMetric] = useState(
    summaryFilters.keyMetric || "Trials"
  );
  const [keyMetricSelectorOpen, setKeyMetricSelectorOpen] = useState(false);
  const taSelectorRef = useRef(null);
  const keyMetricSelectorRef = useRef(null);

  useEffect(() => {
    if (!availableTherapeuticAreas.includes(selectedTA)) {
      setSelectedTA("All");
      onChangeSummaryFilters({
        ...summaryFilters,
        selectedTA: "All",
      });
    }
  }, [availableTherapeuticAreas, selectedTA]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Therapeutic Area Experience Summary
          </h3>
        </div>

        {/* Dropdowns */}
        <div className="flex items-center gap-4">
          {/* TA Selector Dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Therapeutic Area:
            </label>
            <div className="relative" ref={taSelectorRef}>
              <div
                className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm min-w-[320px]"
                onClick={() => setTaSelectorOpen(!taSelectorOpen)}
              >
                <span className="block truncate font-normal">{selectedTA}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </div>

              {taSelectorOpen && (
                <div className="absolute z-30 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-none sm:text-sm max-h-60">
                  {availableTherapeuticAreas.map((ta) => (
                    <div
                      key={ta}
                      className={`relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                        selectedTA === ta
                          ? "bg-indigo-50"
                          : "hover:bg-indigo-50"
                      }`}
                      onClick={() => {
                        setSelectedTA(ta);
                        onChangeSummaryFilters({
                          ...summaryFilters,
                          selectedTA: ta,
                        });
                        setTaSelectorOpen(false);
                      }}
                    >
                      <span
                        className={`block truncate ${
                          selectedTA === ta ? "font-semibold" : "font-normal"
                        }`}
                      >
                        {ta}
                      </span>
                      {selectedTA === ta && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Key Metric Selector Dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Key Metric:
            </label>
            <div className="relative" ref={keyMetricSelectorRef}>
              <div
                className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm min-w-[200px]"
                onClick={() => setKeyMetricSelectorOpen(!keyMetricSelectorOpen)}
              >
                <span className="block truncate font-normal">
                  {selectedKeyMetric}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </div>

              {keyMetricSelectorOpen && (
                <div className="absolute z-30 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-none sm:text-sm">
                  {[
                    "Trials",
                    "Patients",
                    "Therapeutic Areas",
                    "Indications",
                  ].map((metric) => (
                    <div
                      key={metric}
                      className={`relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                        selectedKeyMetric === metric
                          ? "bg-indigo-50"
                          : "hover:bg-indigo-50"
                      }`}
                      onClick={() => {
                        setSelectedKeyMetric(metric);
                        onChangeSummaryFilters({
                          ...summaryFilters,
                          keyMetric: metric,
                        });
                        setKeyMetricSelectorOpen(false);
                      }}
                    >
                      <span
                        className={`block truncate ${
                          selectedKeyMetric === metric
                            ? "font-semibold"
                            : "font-normal"
                        }`}
                      >
                        {metric}
                      </span>
                      {selectedKeyMetric === metric && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-2">
        {Object.entries(vendorSummaryData)
          .sort(([, a], [, b]) => {
            // Sort by selected key metric
            if (selectedKeyMetric === "Trials")
              return b.totalTrials - a.totalTrials;
            if (selectedKeyMetric === "Patients")
              return b.totalPatients - a.totalPatients;
            if (selectedKeyMetric === "Therapeutic Areas")
              return b.therapeuticAreasCount - a.therapeuticAreasCount;
            if (selectedKeyMetric === "Indications")
              return b.indicationsCount - a.indicationsCount;
            return 0;
          })
          .map(([vendor, data], index) => {
            // Get key metric value and label for banner
            const getKeyMetricData = () => {
              if (selectedKeyMetric === "Trials")
                return {
                  value: data.totalTrials.toLocaleString(),
                  label: "Trials",
                };
              if (selectedKeyMetric === "Patients")
                return {
                  value: data.totalPatients.toLocaleString(),
                  label: "Patients",
                };
              if (selectedKeyMetric === "Therapeutic Areas")
                return { value: data.therapeuticAreasCount, label: "TAs" };
              if (selectedKeyMetric === "Indications")
                return { value: data.indicationsCount, label: "Indications" };
              return {
                value: data.totalTrials.toLocaleString(),
                label: "Trials",
              };
            };

            const keyMetricData = getKeyMetricData();

            // Build highlights array - always include all metrics, but swap selected one with Trials
            const allMetrics =
              selectedTA === "All"
                ? {
                    Trials: {
                      label: "Total Trials",
                      value: data.totalTrials.toLocaleString(),
                    },
                    "Therapeutic Areas": {
                      label: "Therapeutic Areas",
                      value: data.therapeuticAreasCount,
                    },
                    Indications: {
                      label: "Unique Indications",
                      value: data.indicationsCount,
                    },
                    Patients: {
                      label: "Total Patients",
                      value: data.totalPatients.toLocaleString(),
                    },
                    "Top Areas": {
                      label: "Top Areas",
                      value: data.therapeuticAreas.slice(0, 2).join(", "),
                    },
                  }
                : {
                    Trials: {
                      label: `Trials in ${selectedTA}`,
                      value: data.totalTrials.toLocaleString(),
                    },
                    Indications: {
                      label: `Indications in ${selectedTA}`,
                      value: data.indicationsCount,
                    },
                    Patients: {
                      label: `Patients in ${selectedTA}`,
                      value: data.totalPatients.toLocaleString(),
                    },
                    "Therapeutic Areas": {
                      label: "All Therapeutic Areas",
                      value: data.therapeuticAreasCount,
                    },
                    "Top Areas": {
                      label: "Top Areas",
                      value: data.therapeuticAreas.slice(0, 2).join(", "),
                    },
                  };

            // Create highlights array - exclude the selected metric since it's in the banner
            const highlights = Object.entries(allMetrics)
              .filter(([key]) => key !== selectedKeyMetric)
              .map(([, metric]) => metric);

            return (
              <div
                key={vendor}
                className="bg-white border border-gray-200 rounded-lg shadow-sm flex-shrink-0 w-80 overflow-hidden"
              >
                <div className="flex items-center gap-4 p-4">
                  <div className="rounded-md bg-gray-100 flex items-center justify-center h-16 w-20 flex-shrink-0">
                    <VendorLogo vendorName={vendor} logoPath={data?.logo} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-indigo-600 transition-colors"
                      onClick={() => {
                        navigate(`/provider/${data?.id}/overview`);
                        window.scrollTo(0, 0);
                      }}
                    >
                      {vendor}
                    </div>
                    <div className="text-xl font-bold text-gray-900 mt-1">
                      {keyMetricData.value} {keyMetricData.label}
                    </div>
                  </div>
                </div>
                <div>
                  {highlights.map((highlight, idx) => (
                    <div
                      key={idx}
                      className={`${
                        idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                      } px-4 py-3 flex justify-between items-center`}
                    >
                      <span className="text-xs text-gray-800">
                        {highlight.label}
                      </span>
                      <span className="text-xs font-semibold text-gray-900 pr-2">
                        {highlight.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
